<template>
  <AdminLayout>
    <v-card elevation="2">
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-account-group" class="mr-2" />
        People
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="d-flex justify-center align-center py-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-alert
          v-else-if="error"
          type="error"
          class="my-4"
        >
          {{ error }}
        </v-alert>
        <div v-else>
          <v-data-table
            :headers="headers"
            :items="users"
            :items-per-page="10"
            class="elevation-0"
          >
            <template v-slot:item.receivedPoints="{ item }">
              <v-chip
                color="success"
                variant="flat"
                size="small"
                class="font-weight-medium"
              >
                {{ item.receivedPoints }}
              </v-chip>
            </template>
            <template v-slot:item.givenPoints="{ item }">
              <v-chip
                color="info"
                variant="flat"
                size="small"
                class="font-weight-medium"
              >
                {{ item.givenPoints }}
              </v-chip>
            </template>
            <template v-slot:item.redeemablePoints="{ item }">
              <v-chip
                color="primary"
                variant="flat"
                size="small"
                class="font-weight-medium"
              >
                <template v-slot:prepend>
                  <span class="mr-1">{{ workspaceEmoji.emojiSymbol }}</span>
                </template>
                {{ item.redeemablePoints }}
              </v-chip>
            </template>
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <span :style="currentUser && currentUser.id === item.name ? 'color: #1976d2; font-weight: 600;' : ''">
                  @{{ item.name }}
                </span>
                <v-chip
                  v-if="!item.active"
                  color="warning"
                  variant="flat"
                  size="x-small"
                  class="ml-2"
                >
                  Inactive
                </v-chip>
              </div>
            </template>

            <template v-slot:item.role="{ item }">
              <div class="d-flex align-center">
                <!-- Show dropdown for other users except current user by handle -->
                <v-select
                  v-if="currentUser && currentUser.id !== item.id && currentUser.id !== item.name"
                  v-model="item.role"
                  :items="['Admin', 'Member']"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :disabled="!canChangeRole(item.id)"
                  @update:model-value="updateUserRole(item.id, $event)"
                ></v-select>
                
                <!-- Show plain text for current user (by id or handle match) -->
                <span v-else class="font-weight-medium" style="margin-left: 4px; width: 100%; text-align: center; display: block;">{{ item.role }}</span>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-center">
                <!-- Show trash can icon for other users except current user -->
                <v-btn
                  v-if="currentUser && currentUser.id !== item.id && currentUser.id !== item.name && item.active"
                  variant="text"
                  color="grey-darken-1"
                  size="small"
                  :disabled="!canChangeRole(item.id)"
                  @click="confirmDeactivateUser(item)"
                >
                  <v-icon icon="mdi-delete" />
                  <v-tooltip activator="parent" location="top">
                    Deactivate User (will auto-reactivate if they participate)
                  </v-tooltip>
                </v-btn>
                
                <!-- Show reactivate button for inactive users -->
                <v-btn
                  v-if="currentUser && currentUser.id !== item.id && currentUser.id !== item.name && !item.active"
                  variant="text"
                  color="success"
                  size="small"
                  :disabled="!canChangeRole(item.id)"
                  @click="reactivateUser(item)"
                >
                  <v-icon icon="mdi-refresh" />
                  <v-tooltip activator="parent" location="top">
                    Reactivate User
                  </v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Deactivate User Confirmation Dialog -->
    <v-dialog v-model="showDeactivateDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon icon="mdi-alert" color="warning" class="mr-2" />
          Confirm User Deactivation
        </v-card-title>
        <v-card-text>
          Are you sure you want to deactivate <strong>@{{ userToDeactivate?.name }}</strong>?
          <br><br>
          This action will:
          <ul class="mt-2">
            <li>Deactivate the user from Prec8</li>
            <li>User's points are kept in case user is reactivated</li>
            <li>Users will be automatically reactivated if they give or receive points</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="showDeactivateDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="deactivateUser"
            :loading="loading"
          >
            Deactivate User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from './components/AdminLayout.vue'
import { useAuthStore } from './stores/authStore'
import { useWorkspaceStore } from './stores/workspaceStore'

interface User {
  id: string
  name: string
  receivedPoints: number
  givenPoints: number
  redeemablePoints: number
  role: string
  active: boolean
}

const router = useRouter()
const { checkAuth } = useAuthStore()
const { getWorkspaceEmoji } = useWorkspaceStore()
const workspaceEmoji = getWorkspaceEmoji()

const users = ref<User[]>([])
const loading = ref(true)
const error = ref('')
const currentUser = ref<{ id: string; isAdmin: boolean } | null>(null)
const showDeactivateDialog = ref(false)
const userToDeactivate = ref<User | null>(null)

// Computed property to check if a user can change a role
const canChangeRole = (userId: string) => {
  if (!currentUser.value) return false
  // Only admins can change roles, and they cannot change their own role
  return currentUser.value.isAdmin && currentUser.value.id !== userId
}

const headers = [
  { title: 'Handle', key: 'name', align: 'start' as const },
  { title: 'Received', key: 'receivedPoints', align: 'center' as const },
  { title: 'Given', key: 'givenPoints', align: 'center' as const },
  { title: 'Redeemable Tokens', key: 'redeemablePoints', align: 'center' as const },
  { title: 'Role', key: 'role', align: 'center' as const },
  { title: 'Actions', key: 'actions', align: 'center' as const }
]

async function updateUserRole(userId: string, newRole: string) {
  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users/${userId}/role`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to update user role')
    }
  } catch (e) {
    console.error('Error updating user role:', e)
    error.value = e instanceof Error ? e.message : 'Failed to update user role. Please try again.'
    // Refresh the users list to revert any UI changes
    await fetchUsers()
  }
}

function confirmDeactivateUser(user: User) {
  userToDeactivate.value = user
  showDeactivateDialog.value = true
}

async function deactivateUser() {
  if (!userToDeactivate.value) return
  
  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users/${userToDeactivate.value.id}`,
      {
        method: 'DELETE',
        credentials: 'include'
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to deactivate user')
    }

    // Update the user's active status in the local list
    const userIndex = users.value.findIndex(user => user.id === userToDeactivate.value!.id)
    if (userIndex !== -1) {
      users.value[userIndex].active = false
    }
    
    // Close dialog and reset
    showDeactivateDialog.value = false
    userToDeactivate.value = null
    
    // Show success message
    error.value = ''
  } catch (e) {
    console.error('Error deactivating user:', e)
    error.value = e instanceof Error ? e.message : 'Failed to deactivate user. Please try again.'
  }
}

async function reactivateUser(user: User) {
  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users/${user.id}/reactivate`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to reactivate user')
    }

    // Update the user's active status in the local list
    const userIndex = users.value.findIndex(u => u.id === user.id)
    if (userIndex !== -1) {
      users.value[userIndex].active = true
    }
    
    // Show success message
    error.value = ''
  } catch (e) {
    console.error('Error reactivating user:', e)
    error.value = e instanceof Error ? e.message : 'Failed to reactivate user. Please try again.'
  }
}

async function fetchUsers() {
  loading.value = true
  error.value = ''
  
  try {
    // Check if user is admin using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      router.push('/dashboard')
      return
    }

    // Set current user info
    currentUser.value = {
      id: authData.userHandle || '',
      isAdmin: authData.isAdmin
    }

    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users`,
      { credentials: 'include' }
    )
    
    if (!response.ok) {
      throw new Error('Failed to fetch users')
    }
    
    const data = await response.json()
    users.value = data.map((user: any) => ({
      id: user.id || '',
      name: user.name || '',
      receivedPoints: user.receivedPoints || 0,
      givenPoints: user.givenPoints || 0,
      redeemablePoints: user.redeemablePoints || 0,
      role: user.role || 'Member',
      active: user.active !== false // Default to true if not specified
    }))
  } catch (e) {
    error.value = 'Failed to load users. Please try again later.'
    console.error('Error fetching users:', e)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchUsers()
})
</script> 